import React, { memo, useCallback, useEffect } from 'react';
import { useSelector } from 'react-redux';

import { FormControlLabel, Switch } from '@mui/material';
import { DeleteIcon } from '@sharedCR/coreDesignSystem/icons';
import ButtonWithTooltip from 'app/shared/components/common/ButtonWithTooltip';
import { FormAutocompleteOption } from 'app/shared/components/form/Autocomplete';
import _ from 'lodash';
import PropTypes from 'prop-types';

import { FieldTypesEnum, LoanEditorActionsEnum } from 'constants/loanActionEditor';
import { deleteByIndex, setIn } from 'utils/data';

import {
  selectChatAssignmentCriteriaSchema,
  selectLoanRoleAssignmentCriteriaSchema,
} from 'store/system/selectors';

import ExpressionField from '../../form/ExpressionField';
import { loanManagementTestIds } from '../loanManagement.testIds';

import useFormContext from './hooks/useFormContext';
import { wrapperClassname } from './LoanActionItem.constants';
import { DynamicValueInput } from './LoanActionItems';
import { groupFieldsFunction, LoanEditorItemsKeyByType } from './LoanActionsGroup.utils';

const LoanActionCriteriaRow = ({
  index,
  rootPath,
  options,
  isDisabled,
  isEditAllowed,
  isSingleItem,
  label = '',
  itemType = '',
  isAutomatedActions = false,
}) => {
  const {
    form,
    setInForm,
    setValidationErrors,
    validationErrors,
    validateField,
    staticValidateField,
    handleChange,
  } = useFormContext();
  const loanRoleCriteriaSchemaParams = useSelector(selectLoanRoleAssignmentCriteriaSchema);
  const chatCriteriaSchemaParams = useSelector(selectChatAssignmentCriteriaSchema);

  const isAssignLoanRole = itemType === LoanEditorActionsEnum.ASSIGN_LOAN_ROLE;

  const CriteriaActionExpressionFieldConfig = {
    loanRole: {
      key: LoanEditorItemsKeyByType[LoanEditorActionsEnum.ASSIGN_LOAN_ROLE],
      label: 'Loan Role',
      id: 'loanRoleCriteriaFriendlyExpression',
      schemaParams: loanRoleCriteriaSchemaParams,
      isWithUserSelection: isAssignLoanRole,
      userSelectionFieldName: 'allowUserSelection',
    },
    chatCriteria: {
      key: LoanEditorItemsKeyByType[LoanEditorActionsEnum.ASSIGN_CHAT_MEMBERS],
      label: 'Chat',
      id: 'chatCriteriaFriendlyExpression',
      schemaParams: chatCriteriaSchemaParams,
      isRequired: true,
    },
  };

  const itemKey = LoanEditorItemsKeyByType[itemType];
  const { id, isRequired, key, schemaParams, isWithUserSelection, userSelectionFieldName } =
    CriteriaActionExpressionFieldConfig[itemKey];
  const canShowUserSelection = isWithUserSelection && !isAutomatedActions;

  const formKey = `${rootPath}[${index}]`;
  const criteriaExpressionPath = `${formKey}.${id}`;
  const dynamicValuePath = `${formKey}.${key}`;
  const allowUserSelectionValuePath = `${formKey}.${userSelectionFieldName}`;
  const criteriaFriendlyExpressionValue = _.get(form, criteriaExpressionPath);
  const allowUserSelectionValue = _.get(form, allowUserSelectionValuePath);
  const criteriaValidationError = _.get(validationErrors, criteriaExpressionPath);

  const forms = _.get(form, rootPath);

  const onDeleteRow = useCallback(() => {
    const newForms = [...forms];
    newForms.splice(index, 1);
    setInForm(rootPath, newForms);
    setValidationErrors(errors =>
      setIn(errors, rootPath, deleteByIndex(_.get(errors, rootPath, []), index)),
    );
  }, [setInForm, setValidationErrors, index, forms, rootPath]);

  const onAllowUserSelectionChange = useCallback(
    e => {
      const isChecked = e.target.checked;

      if (isChecked) {
        setInForm(criteriaExpressionPath, '');
      }

      setInForm(allowUserSelectionValuePath, e.target.checked);
    },
    [criteriaExpressionPath, setInForm, allowUserSelectionValuePath],
  );

  useEffect(() => {
    if (!isAutomatedActions || !isWithUserSelection || allowUserSelectionValue === false) {
      return;
    }

    setInForm(allowUserSelectionValuePath, false);
  }, [
    isAutomatedActions,
    isWithUserSelection,
    allowUserSelectionValue,
    allowUserSelectionValuePath,
    setInForm,
  ]);

  return (
    <div className={wrapperClassname}>
      <div className="flex">
        <DynamicValueInput
          required
          OptionContent={itemKey === 'loanRole' ? FormAutocompleteOption : undefined}
          className="grow"
          disabled={isDisabled || !isEditAllowed}
          groupBy={groupFieldsFunction}
          label={label}
          name={dynamicValuePath}
          options={options}
          type={FieldTypesEnum.SELECT}
        />
        {isEditAllowed && !isSingleItem && (
          <ButtonWithTooltip
            data-testid={loanManagementTestIds.getDeleteRowButtonId(formKey)}
            disabled={isDisabled}
            icon={DeleteIcon}
            size="medium"
            spanContainerClassName="flex items-center"
            title="Delete row"
            onClick={onDeleteRow}
          />
        )}
      </div>
      {canShowUserSelection && (
        <FormControlLabel
          control={
            <Switch checked={allowUserSelectionValue} onChange={onAllowUserSelectionChange} />
          }
          data-testid={loanManagementTestIds.getAllowUserSelectionSwitchId(formKey)}
          label="Allow User Selection"
        />
      )}
      {(!canShowUserSelection || !allowUserSelectionValue) && (
        <ExpressionField
          isOnlyBooleanAllowed
          className="mb-24"
          dataTestId={id}
          disabled={isDisabled || !isEditAllowed}
          error={!!criteriaValidationError}
          handleChange={handleChange}
          helperText={criteriaValidationError}
          id={id}
          label={`${label} Assignment Criteria`}
          name={criteriaExpressionPath}
          required={!!isRequired}
          schemaParams={schemaParams}
          setInForm={setInForm}
          staticValidateField={staticValidateField}
          validateField={validateField}
          value={criteriaFriendlyExpressionValue}
        />
      )}
    </div>
  );
};

LoanActionCriteriaRow.propTypes = {
  isSingleItem: PropTypes.bool.isRequired,
  rootPath: PropTypes.string.isRequired,
  index: PropTypes.number.isRequired,
  options: PropTypes.array.isRequired,
  isDisabled: PropTypes.bool.isRequired,
  isEditAllowed: PropTypes.bool.isRequired,
  label: PropTypes.string,
  itemType: PropTypes.string,
  isAutomatedActions: PropTypes.bool,
};

export default memo(LoanActionCriteriaRow);
